<header class="fixed left-0 top-0 z-30 w-full border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
  <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
    <div class="flex items-center gap-2 cursor-pointer select-none" (click)="goToHome()">
      <div class="grid h-9 w-9 place-items-center rounded-xl">
        <img src="favicon.png" alt="Confocean" class="h-6 w-6" />
      </div>
      <span class="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Confocean</span>
    </div>

    <div class="flex items-center gap-2">
      <button
        mat-icon-button
        (click)="themeService.toggleTheme()"
        [attr.aria-label]="themeService.isDark() ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
        class="w-9 h-9 rounded-lg transition-all duration-200 hover:bg-slate-100 dark:hover:bg-slate-700"
        [matTooltip]="themeService.isDark() ? 'Light Mode' : 'Dark Mode'">
        <mat-icon class="text-slate-600 dark:text-slate-300">
          {{ themeService.isDark() ? 'light_mode' : 'dark_mode' }}
        </mat-icon>
      </button>
      <button
        class="cursor-pointer rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-900 dark:text-white shadow-sm shadow-slate-900/5 transition hover:bg-slate-50 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1e3a8a]/30"
        (click)="signIn()"
      >
        Sign in
      </button>
    </div>
  </div>
</header>

<div class="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950 pt-20 pb-24">
  <div class="mx-auto grid max-w-7xl grid-cols-1 items-start gap-10 px-4 lg:grid-cols-2 lg:gap-16 lg:pt-10">
    <div class="hidden lg:flex lg:flex-col lg:items-center lg:justify-center lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)]">
      <div class="max-w-xl">
        <h1 class="text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">Create your ConfOcean account.</h1>
        <p class="mt-4 text-base leading-relaxed text-slate-600 dark:text-slate-400">
          Start organizing events, tracking registrations, and managing content — all from one dashboard.
        </p>
        <div class="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 p-4 shadow-sm backdrop-blur">
            <div class="text-sm font-semibold text-slate-900 dark:text-white">Professional setup</div>
            <div class="mt-1 text-sm text-slate-600 dark:text-slate-400">Add your organization and website in minutes.</div>
          </div>
          <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 p-4 shadow-sm backdrop-blur">
            <div class="text-sm font-semibold text-slate-900 dark:text-white">Simple billing</div>
            <div class="mt-1 text-sm text-slate-600 dark:text-slate-400">Choose a plan that fits your event cycle.</div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-center lg:justify-end">
      <div class="w-full max-w-md">
        <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 p-6 shadow-xl shadow-slate-900/5 dark:shadow-black/20 backdrop-blur sm:p-8">

          <!-- Success Message -->
          <div *ngIf="signupSuccess" class="text-center py-8">
            <div class="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-5">
              <mat-icon class="text-emerald-600 dark:text-emerald-400 text-4xl">check_circle</mat-icon>
            </div>
            <h2 class="text-xl font-semibold text-slate-900 dark:text-white mb-2">Account Created Successfully!</h2>
            <p class="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
              Thank you for signing up. Our admin team will review your account and connect with you shortly.
            </p>
            <p class="text-slate-500 dark:text-slate-500 text-xs mt-4">
              You will receive an email once your account is activated.
            </p>
            <button
              (click)="signIn()"
              class="mt-6 cursor-pointer rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-6 py-2 text-sm font-semibold text-slate-900 dark:text-white shadow-sm transition hover:bg-slate-50 dark:hover:bg-slate-700">
              Back to Sign in
            </button>
          </div>

          <!-- Sign Up Form -->
          <div *ngIf="!signupSuccess">
          <div class="flex items-center gap-3">
            <div class="grid h-10 w-10 place-items-center rounded-2xl">
              <img src="favicon.png" alt="Confocean" class="h-6 w-6" />
            </div>
            <div>
              <h2 class="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Create account</h2>
              <p class="mt-0.5 text-sm text-slate-600 dark:text-slate-400">It takes less than a minute.</p>
            </div>
          </div>

          <form
            [formGroup]="signupForm"
            (ngSubmit)="signUp(signupForm.value)"
            novalidate
            autocomplete="off"
            class="mt-6 space-y-4"
          >
            <div>
              <mat-form-field appearance="outline" class="w-full">
                <input matInput placeholder="Full name" formControlName="full_name" class="text-base" />
                <mat-icon matPrefix>person</mat-icon>
              </mat-form-field>
            </div>

            <div>
              <mat-form-field appearance="outline" class="w-full" [subscriptSizing]="'dynamic'">
                <input matInput placeholder="Business email" formControlName="email" class="text-base" [readonly]="emailVerified" (paste)="onEmailPaste($event)" (input)="onEmailInput()" />
                <mat-icon matPrefix>mail</mat-icon>
                <mat-error *ngIf="signupForm.get('email')?.hasError('required')">Email is required</mat-error>
                <mat-error *ngIf="signupForm.get('email')?.hasError('invalidEmail')">
                  Please enter a valid email address (e.g., user&#64;domain.com)
                </mat-error>
              </mat-form-field>
              <!-- Email verification -->
              <div *ngIf="!emailVerified" class="flex items-center gap-2 mt-1 mb-2">
                <button type="button"
                  (click)="sendEmailOtp()"
                  [disabled]="sendingEmailOtp || signupForm.get('email')?.invalid || emailOtpSent"
                  class="cursor-pointer text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  {{ sendingEmailOtp ? 'Sending...' : (emailOtpSent ? 'OTP Sent' : 'Send OTP') }}
                </button>
                <div *ngIf="emailOtpSent" class="flex items-center gap-2 flex-1">
                  <input type="text" [(ngModel)]="emailOtp" [ngModelOptions]="{standalone: true}"
                    placeholder="Enter OTP" maxlength="6"
                    class="w-24 px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                  <button type="button"
                    (click)="verifyEmailOtp()"
                    [disabled]="verifyingEmail || !emailOtp"
                    class="cursor-pointer text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ verifyingEmail ? 'Verifying...' : 'Verify' }}
                  </button>
                  <button type="button" (click)="emailOtpSent = false; emailOtp = ''"
                    class="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline">
                    Resend
                  </button>
                </div>
              </div>
              <div *ngIf="emailVerified" class="flex items-center gap-1.5 mt-1 mb-2 text-emerald-600 dark:text-emerald-400">
                <mat-icon class="text-base">check_circle</mat-icon>
                <span class="text-xs font-medium">Email verified</span>
              </div>
            </div>

            <div>
              <mat-form-field appearance="outline" class="w-full">
                <input matInput placeholder="Mobile" formControlName="mobile" class="text-base" />
                <mat-icon matPrefix>phone</mat-icon>
              </mat-form-field>
            </div>

            <div>
              <mat-form-field appearance="outline" class="w-full">
                <input matInput placeholder="Organization" formControlName="organization" class="text-base" />
                <mat-icon matPrefix>business</mat-icon>
              </mat-form-field>
            </div>

            <div>
              <mat-form-field appearance="outline" class="w-full">
                <input matInput placeholder="Website" formControlName="website_url" class="text-base" />
                <mat-icon matPrefix>language</mat-icon>
              </mat-form-field>
            </div>

            <div>
              <mat-form-field appearance="outline" class="w-full">
                <mat-select formControlName="subscription_plan" placeholder="Select Plan" (selectionChange)="onPlanChange()">
                  <mat-option *ngFor="let plan of plans" [value]="plan.id">{{ plan.name }}</mat-option>
                </mat-select>
                <mat-icon matPrefix>subscriptions</mat-icon>
              </mat-form-field>
            </div>

            <!-- Selected Plan Details -->
            <div *ngIf="selectedPlan"
                 class="rounded-xl border border-indigo-200 dark:border-indigo-700 bg-indigo-50/70 dark:bg-indigo-900/20 p-4 space-y-3">
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-semibold text-indigo-900 dark:text-indigo-200">{{ selectedPlan.name }}</h4>
                <span class="text-lg font-bold text-indigo-700 dark:text-indigo-300">₹{{ selectedPlan.price }}<span class="text-xs font-normal text-slate-500 dark:text-slate-400"> / {{ selectedPlan.billing_cycle_display }}</span></span>
              </div>
              <p *ngIf="selectedPlan.description" class="text-xs text-slate-600 dark:text-slate-400">{{ selectedPlan.description }}</p>
              <div class="grid grid-cols-3 gap-2 text-xs">
                <div class="rounded-lg bg-white dark:bg-slate-800 px-2 py-1.5 text-center border border-slate-200 dark:border-slate-700">
                  <div class="font-semibold text-slate-900 dark:text-white">{{ selectedPlan.max_events === 0 ? '∞' : selectedPlan.max_events }}</div>
                  <div class="text-slate-500 dark:text-slate-400">Events</div>
                </div>
                <div class="rounded-lg bg-white dark:bg-slate-800 px-2 py-1.5 text-center border border-slate-200 dark:border-slate-700">
                  <div class="font-semibold text-slate-900 dark:text-white">{{ selectedPlan.max_users === 0 ? '∞' : selectedPlan.max_users }}</div>
                  <div class="text-slate-500 dark:text-slate-400">Users</div>
                </div>
                <div class="rounded-lg bg-white dark:bg-slate-800 px-2 py-1.5 text-center border border-slate-200 dark:border-slate-700">
                  <div class="font-semibold text-slate-900 dark:text-white">{{ selectedPlan.max_registrations_per_event === 0 ? '∞' : selectedPlan.max_registrations_per_event }}</div>
                  <div class="text-slate-500 dark:text-slate-400">Reg/Event</div>
                </div>
              </div>
              <!-- Modules Included -->
              <div *ngIf="selectedPlan.modules_included && getModuleKeys(selectedPlan.modules_included).length > 0">
                <div class="text-xs font-semibold text-indigo-900 dark:text-indigo-200 mb-1.5">Modules Included</div>
                <div class="flex flex-wrap gap-1.5">
                  <span *ngFor="let module of getModuleKeys(selectedPlan.modules_included)"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border"
                    [ngClass]="selectedPlan.modules_included[module] ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700' : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-600 line-through'">
                    <mat-icon class="text-xs" style="font-size: 12px; width: 12px; height: 12px;">
                      {{ selectedPlan.modules_included[module] ? 'check_circle' : 'cancel' }}
                    </mat-icon>
                    {{ module | titlecase }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Payment Method -->
            <div *ngIf="selectedPlan" class="space-y-3">
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Choose your Payment Method for your manual balance top up <span class="text-red-500">*</span>
              </label>
              <div class="space-y-2">
                <label *ngFor="let method of paymentMethods"
                  class="flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition"
                  [ngClass]="signupForm.get('payment_method')?.value === method.value
                    ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-900/20 dark:border-blue-400 ring-1 ring-blue-500 dark:ring-blue-400'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'">
                  <input type="radio" formControlName="payment_method" [value]="method.value"
                    class="h-4 w-4 cursor-pointer text-blue-600 border-slate-300 dark:border-slate-600 focus:ring-blue-500" />
                  <mat-icon class="text-slate-600 dark:text-slate-300" style="font-size: 20px; width: 20px; height: 20px;">{{ method.icon }}</mat-icon>
                  <span class="text-sm font-medium text-slate-900 dark:text-white">{{ method.label }}</span>
                </label>
              </div>

              <div *ngIf="signupForm.get('payment_method')?.value">
                <mat-form-field appearance="outline" class="w-full">
                  <input matInput placeholder="Transaction ID / Auth ID" formControlName="transaction_id" class="text-base" />
                  <mat-icon matPrefix>confirmation_number</mat-icon>
                </mat-form-field>
              </div>
            </div>

            <button
              type="submit"
              [disabled]="!canSubmit"
              class="cursor-pointer w-full rounded-full bg-[#1e3a8a] py-2.5 text-base font-semibold text-white shadow-sm shadow-blue-900/10 transition hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1e3a8a]/40 disabled:opacity-50"
            >
              Sign up
            </button>
            <p *ngIf="signupForm.valid && !emailVerified" class="text-xs text-amber-600 dark:text-amber-400 text-center -mt-2">
              Please verify your email to continue
            </p>
          </form>
          </div>
        </div>

        <div class="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
          By creating an account, you agree to Confocean's terms and privacy policy.
        </div>
      </div>
    </div>
  </div>
</div>

<footer class="w-full border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 py-6 backdrop-blur md:fixed md:bottom-0 md:left-0">
  <div class="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 md:flex-row md:gap-0">
    <div class="flex items-center gap-2">
      <img src="favicon.png" alt="Confocean" class="h-6 w-6" />
      <span class="text-sm font-semibold text-slate-900 dark:text-white">Confocean</span>
      <span class="text-sm text-slate-500 dark:text-slate-400">© {{ currentYear }}</span>
    </div>
    <nav class="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
      <a href="https://confocean.com/user-agreement" target="_blank" class="hover:text-slate-900 dark:hover:text-white hover:underline">User Agreement</a>
      <a href="https://confocean.com/privacy-policy" target="_blank" class="hover:text-slate-900 dark:hover:text-white hover:underline">Privacy Policy</a>
      <a href="https://confocean.com/cookies-policy" target="_blank" class="hover:text-slate-900 dark:hover:text-white hover:underline">Cookie Policy</a>
      <a href="https://confocean.com/copyright-policy" target="_blank" class="hover:text-slate-900 dark:hover:text-white hover:underline">Copyright Policy</a>
    </nav>
  </div>
</footer>
